export interface DocumentationHeading {
  id: string;
  text: string;
}

interface LexicalNode {
  type?: string;
  tag?: string;
  text?: string;
  fields?: { content?: unknown };
  children?: LexicalNode[];
}

export const slugifyHeading = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const textOf = (node: LexicalNode): string => {
  if (typeof node.text === 'string') return node.text;
  if (node.type === 'block' && typeof node.fields?.content === 'string') return node.fields.content;
  return (node.children ?? []).map(textOf).join('');
};

const walk = (node: LexicalNode, into: LexicalNode[]): void => {
  if (node.type === 'heading' && node.tag === 'h2') into.push(node);
  (node.children ?? []).forEach((child) => walk(child, into));
};

const anchorsFor = (nodes: LexicalNode[]): string[] => {
  const seen = new Map<string, number>();

  return nodes.map((node) => {
    const base = slugifyHeading(textOf(node).trim()) || 'section';
    const taken = seen.get(base) ?? 0;

    seen.set(base, taken + 1);

    return taken === 0 ? base : `${base}-${taken + 1}`;
  });
};

const headingNodesOf = (body: unknown): LexicalNode[] => {
  const root = (body as { root?: LexicalNode } | null)?.root;
  if (!root) return [];

  const found: LexicalNode[] = [];
  walk(root, found);

  return found;
};

/**
 * The anchor of every `h2`, keyed by the node itself, so a reader renders the id the sidebar links to
 * however many times React re-renders the page.
 */
export const headingAnchors = (body: unknown): Map<LexicalNode, string> => {
  const nodes = headingNodesOf(body);
  const ids = anchorsFor(nodes);

  return new Map(nodes.map((node, index) => [node, ids[index]]));
};

/** The `h2` nodes of a stored body, in order, with a stable anchor each. Duplicates get a suffix. */
export const collectHeadings = (body: unknown): DocumentationHeading[] =>
  [...headingAnchors(body)].flatMap(([node, id]) => {
    const text = textOf(node).trim();

    return text ? [{ id, text }] : [];
  });

/** Every word of a page a reader could reasonably search for. */
export const plainTextOf = (body: unknown): string => {
  const root = (body as { root?: LexicalNode } | null)?.root;
  return root ? textOf(root) : '';
};
